import { useState, useEffect } from 'react'
import { Capacitor } from '@capacitor/core'

type BeforeInstallPromptEvent = Event & {
	prompt: () => void
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface UsePWAInstallResult {
	canInstall: boolean
	errorMessage: string
	handleInstallClick: () => Promise<void>
}

interface UsePwaInstallParams {
	onError: () => void
}

export const usePWAInstall = (params?: UsePwaInstallParams): UsePWAInstallResult => {
	const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
	const [canInstall, setCanInstall] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string>('')

	useEffect(() => {
		if (Capacitor.isNativePlatform()) {
			setCanInstall(false)
			setErrorMessage('')
			return
		}

		const handleBeforeInstallPrompt = (event: Event) => {
			event.preventDefault()
			setDeferredPrompt(event as BeforeInstallPromptEvent)
			setCanInstall(true)
			console.log('Событие beforeinstallprompt перехвачено. Установка возможна.')
		}

		const handleAppInstalled = () => {
			setCanInstall(false)
			console.log('Приложение успешно установлено.')
		}

		const checkInstallability = () => {
			if (!('BeforeInstallPromptEvent' in window)) {
				setErrorMessage('Ваш браузер не поддерживает установку PWA.')
				console.log(
					'Причина невозможности установки: Browser does not support BeforeInstallPromptEvent.'
				)
			} else if (!window.navigator.serviceWorker) {
				setErrorMessage(
					'Service Worker не зарегистрирован. PWA требует Service Worker для установки.'
				)
				console.log('Причина невозможности установки: Service Worker not registered.')
			} else {
				setErrorMessage('')
				console.log(
					'Проверка на возможность установки завершена. Ожидание события beforeinstallprompt.'
				)
			}
		}

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
		window.addEventListener('appinstalled', handleAppInstalled)

		checkInstallability()

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
			window.removeEventListener('appinstalled', handleAppInstalled)
		}
	}, [])

	const handleInstallClick = async () => {
		try {
			if (deferredPrompt) {
				console.log('Вызов prompt() для установки PWA.')
				await deferredPrompt.prompt()
				const { outcome } = await deferredPrompt.userChoice

				if (outcome === 'accepted') {
					console.log('Пользователь согласился установить PWA.')
				} else {
					console.log('Пользователь отказался от установки PWA. Результат: ' + outcome)
					params?.onError?.()
				}

				setDeferredPrompt(null)
				setCanInstall(false)
			} else {
				const message = 'Приложение уже установлено или не может быть установлено.'
				setErrorMessage(message)
				console.log(
					'Попытка установки PWA, но deferredPrompt отсутствует. Причина: ' + message
				)
				params?.onError?.()
			}
		} catch (error) {
			const message = 'Произошла ошибка при попытке установки: ' + error
			setErrorMessage(message)
			console.error('Ошибка при установке PWA: ', error)
			params?.onError?.()
		}
	}

	return { canInstall, errorMessage, handleInstallClick }
}
